import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "@/lib/api";
import Seo from "@/components/Seo";
import SectionReveal from "@/components/SectionReveal";
import { ADDRESS } from "@/components/LandingBits";
import { trackCta, trackFormStart, trackLead } from "@/lib/analytics";
import { sendEvent, attribution } from "@/lib/tracking";

const PATH = "/fuel-chapters";
const BENEFITS = [
  { t: "Understand nutrition", d: "Clear, practical explanations — no fads, no jargon." },
  { t: "Ask Georgie your questions", d: "Bring the things you've always wondered about and get straight answers." },
  { t: "Everyday habit support", d: "Help with the eating habits that actually shape your week." },
  { t: "Group accountability", d: "Consistency is easier alongside people working on the same things." },
];

export default function FuelChapters() {
  const [f, setF] = useState({ first_name: "", email: "", mobile: "", consent: false });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [started, setStarted] = useState(false);

  const start = () => { if (!started) { setStarted(true); trackFormStart({ form_name: "fuel_chapters_interest" }); sendEvent("form_start", { funnel: "fuel" }); } };

  const submit = async (e) => {
    e.preventDefault();
    const er = {};
    if (!f.first_name.trim()) er.first_name = "Please add your first name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) er.email = "Please enter a valid email address.";
    if (f.mobile.trim() && !/^\+?[\d\s()-]{10,16}$/.test(f.mobile.trim())) er.mobile = "Please enter a valid UK mobile number, or leave blank.";
    setErrors(er);
    if (Object.keys(er).length || busy) return;
    setBusy(true);
    try {
      await api.post("/leads", { first_name: f.first_name.trim(), email: f.email.trim(), mobile: f.mobile.trim() || null, consent: f.consent, funnel: "fuel", page_source: PATH, ...attribution() });
      trackLead({ form_name: "fuel_chapters_interest", enquiry_type: "fuel" });
      sendEvent("form_submit", { funnel: "fuel" });
      setDone(true);
    } catch (err) { toast.error(errText(err) || "We couldn't send that just now — please try again."); }
    finally { setBusy(false); }
  };

  const input = "mt-2 w-full bg-white border border-kore-stone rounded-lg px-4 py-3 min-h-[44px] text-kore-ink focus:outline-none focus:border-kore-forest focus:ring-2 focus:ring-kore-forest/20";
  const label = "text-[14px] font-semibold text-kore-forest";
  const err = (k) => errors[k] && <span data-testid={`fuel-error-${k}`} className="block mt-1 text-[13px] text-red-700">{errors[k]}</span>;

  return (
    <div data-testid="fuel-chapters-page" className="bg-kore-warm">
      <Seo
        title="Fuel Chapters | Nutrition Group in Bowness"
        description="Explore Fuel Chapters at KORE Wellbeing in Bowness-on-Windermere. Thursday nutrition education, practical habit support and accountability with Georgie."
        path={PATH}
      />

      <section className="container-kore pt-16 pb-16 md:pb-24 grid lg:grid-cols-12 gap-12 items-end">
        <div className="lg:col-span-7">
          <p className="eyebrow">Fuel Chapters · At KORE</p>
          <h1 className="display-1 text-kore-forest mt-5">Make sense of nutrition.<br />Make changes that stick.</h1>
          <p className="mt-7 text-lg md:text-xl text-kore-ink/80 leading-relaxed max-w-xl">Join Georgie, KORE's nutritionist, for a Thursday group bringing together practical nutrition education, honest conversations and accountability.</p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            <a href="#interest" data-testid="fuel-cta-primary" onClick={() => trackCta({ cta_name: "fuel_register", cta_text: "Register your interest", destination_url: "#interest", cta_location: "hero" })} className="btn-primary">Register your interest <ArrowRight size={16} /></a>
            <Link to="/contact" data-testid="fuel-cta-secondary" className="btn-ghost">Ask us a question</Link>
          </div>
        </div>
        <div className="lg:col-span-5">
          <figure>
            <div className="rounded-2xl overflow-hidden aspect-[4/5] bg-kore-stone"><img src="/gallery/fuel-chapters-poster.webp" alt="Fuel Chapters promotional artwork — your weekly nutrition reset, Thursday meet-ups at KORE" loading="eager" className="w-full h-full object-cover" /></div>
            <figcaption className="mt-3 text-[13px] text-kore-ink/55">Fuel Chapters artwork. Group photography to follow.</figcaption>
          </figure>
        </div>
      </section>

      <SectionReveal as="section" data-testid="fuel-benefits" className="bg-kore-stone py-24 md:py-32">
        <div className="container-kore">
          <div className="max-w-2xl">
            <p className="eyebrow">The experience</p>
            <h2 className="display-2 text-kore-forest mt-4">A place to learn, ask and put it into practice.</h2>
          </div>
          <ul className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {BENEFITS.map((b, i) => (
              <li key={b.t} data-testid={`fuel-benefit-${i}`} className="bg-white border border-kore-stone rounded-2xl p-7">
                <div className="text-kore-forest font-semibold">0{i + 1}</div>
                <h3 className="mt-3 text-xl font-semibold text-kore-forest">{b.t}</h3>
                <p className="mt-2 text-[15px] text-kore-ink/70 leading-relaxed">{b.d}</p>
              </li>
            ))}
          </ul>
        </div>
      </SectionReveal>

      <SectionReveal as="section" data-testid="fuel-georgie" className="bg-kore-warm py-24 md:py-32">
        <div className="container-kore grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <p className="eyebrow">Meet Georgie</p>
            <h2 className="display-2 text-kore-forest mt-4">KORE's nutritionist.</h2>
            <div className="mt-6 space-y-4 text-kore-ink/80 leading-relaxed">
              <p>"Food has always fascinated me — not just how it tastes, but the science behind it. Understanding how food fuels our bodies, impacts our health and influences our wellbeing led me to pursue Nutrition Science."</p>
              <p>"I believe food is something we should all enjoy, not fear. My goal is to help people build a healthier, more balanced relationship with food — one that nourishes both body and mind. Whether you're looking to improve your nutrition, develop sustainable habits or simply feel better in your own skin, I'm here to guide you with evidence-based advice and a realistic approach to eating well."</p>
            </div>
            <p className="mt-6 text-[15px] text-kore-ink/60">Georgie leads Fuel Chapters every Thursday at KORE.</p>
          </div>
          <div className="md:col-span-4 md:col-start-9">
            <div className="rounded-2xl bg-kore-stone aspect-[3/4] flex items-end p-6"><p className="text-[13px] text-kore-ink/55">Portrait of Georgie to follow.</p></div>
          </div>
        </div>
      </SectionReveal>

      <SectionReveal as="section" data-testid="fuel-details" className="bg-kore-warm pb-24 md:pb-32">
        <div className="container-kore grid sm:grid-cols-2 gap-8 border-t border-kore-stone pt-12 max-w-4xl">
          <div className="flex gap-3"><CalendarDays size={20} className="text-kore-forest mt-1 shrink-0" /><div><p className="eyebrow">When</p><p className="mt-2 text-kore-ink/80">Thursdays. Register your interest and we'll send you the current times and how to join.</p></div></div>
          <div className="flex gap-3"><MapPin size={20} className="text-kore-forest mt-1 shrink-0" /><div><p className="eyebrow">Where</p><p className="mt-2 text-kore-ink/80">{ADDRESS.name}, {ADDRESS.street}, {ADDRESS.locality}, {ADDRESS.region} {ADDRESS.postcode}</p></div></div>
        </div>
      </SectionReveal>

      <section id="interest" data-testid="fuel-interest" className="bg-kore-forest text-white py-24 md:py-32 scroll-mt-20">
        <div className="container-kore grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="eyebrow text-kore-light">Register your interest</p>
            <h2 className="display-2 mt-4">Interested in joining us?</h2>
            <p className="mt-6 text-white/75 leading-relaxed">Leave your details and we'll get back to you with information about Fuel Chapters and how to join.</p>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            {done ? (
              <div data-testid="fuel-success" className="bg-white/10 rounded-2xl p-8 md:p-10">
                <h3 className="text-2xl font-semibold">Thanks for your interest in Fuel Chapters.</h3>
                <p className="mt-3 text-white/80 leading-relaxed">We've received your enquiry and will be in touch with the details.</p>
              </div>
            ) : (
              <form onSubmit={submit} onFocus={start} noValidate data-testid="fuel-form" className="bg-white rounded-2xl p-8 md:p-10 text-kore-ink space-y-5">
                <label className="block"><span className={label}>First name</span><input data-testid="fuel-first-name" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} className={input} autoComplete="given-name" />{err("first_name")}</label>
                <label className="block"><span className={label}>Email address</span><input data-testid="fuel-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={input} autoComplete="email" />{err("email")}</label>
                <label className="block"><span className={label}>Mobile number <span className="font-normal text-kore-ink/55">(optional)</span></span><input data-testid="fuel-mobile" type="tel" value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} className={input} autoComplete="tel" />{err("mobile")}</label>
                <label className="flex items-start gap-3 text-[14px] text-kore-ink/70"><input type="checkbox" data-testid="fuel-consent" checked={f.consent} onChange={(e) => setF({ ...f, consent: e.target.checked })} className="mt-1 h-4 w-4" /><span>Keep me posted about KORE news and offers (optional). See our <Link to="/privacy" className="underline">privacy notice</Link>.</span></label>
                <button disabled={busy} data-testid="fuel-submit" className="btn-primary w-full justify-center">{busy ? "Sending…" : "Register my interest"} <ArrowRight size={16} /></button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
